import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, useCurrentCompany, useMe } from '../../lib'
import { Empty, ErrorBox, Loading } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import { useSide } from '../../app/side'
import { LAYER_LABEL, PartnerCard, PartnerDialog } from './parts'
import type { DirectoryCard, DirectoryList } from './types'

const LAYERS: [string, string][] = [['', 'All'], ...Object.entries(LAYER_LABEL)]

export function DirectoryPage() {
  const [params, setParams] = useSearchParams()
  const layer = params.get('layer') ?? ''
  const q = params.get('q') ?? ''
  const { me } = useMe()
  const company = useCurrentCompany()
  const { side } = useSide()
  const signedIn = !!me?.user && !!company && side === 'company'
  const [draft, setDraft] = useState(q)
  const [open, setOpen] = useState<DirectoryCard | null>(null)

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const qs = new URLSearchParams()
  if (layer) qs.set('layer', layer)
  if (q) qs.set('q', q)
  if (signedIn) qs.set('company_id', company!.id)
  else if (me?.user) qs.set('view', 'public')   // off the company side: the public offer, not a company's status

  const list = useQuery({
    queryKey: ['web', 'directory', qs.toString(), me?.user?.id ?? null],
    queryFn: () => api<DirectoryList>('GET', `/api/directory?${qs}`),
    placeholderData: keepPreviousData,
    enabled: me !== undefined,
  })

  return (
    <div className="stack e-wide" data-testid="web-directory">
      <PageBand title={signedIn || side === 'company' ? 'Partners' : 'Partner directory'} sub="Points pay back as cash on what you pay these partners." />

      <div className="filters">
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            set('q', draft.trim())
          }}
        >
          <input
            type="search"
            placeholder="Search"
            aria-label="Search partners"
            value={draft}
            data-testid="web-directory-search"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => set('q', draft.trim())}
          />
        </form>
        <div className="chips" role="group" aria-label="Layer">
          {LAYERS.map(([value, label]) => (
            <button
              key={value || 'all'}
              type="button"
              aria-pressed={layer === value}
              data-testid={`web-layer-${value || 'all'}`}
              onClick={() => set('layer', value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {list.isLoading && <Loading what="Loading partners" />}
      {list.error && <ErrorBox error={list.error} />}
      {list.data && list.data.rows.length === 0 && <Empty>No partners match.</Empty>}
      {list.data && list.data.rows.length > 0 && (
        <div className="cards">
          {list.data.rows.map((c) => <PartnerCard key={c.id} card={c} onOpen={setOpen} />)}
        </div>
      )}
      {open && <PartnerDialog card={open} signedIn={!!me?.user} side={side} company={company} onClose={() => setOpen(null)} />}

    </div>
  )
}
